/**
 * Product reviews: featured written reviews on the product page.
 *
 * Fetches through the same Supabase edge function the React app uses, so both
 * storefronts read one source and the Judge.me API key stays server-side.
 *
 * Cards match the homepage strip (cs-reviews-strip.js) — same ink card, same
 * 190-character truncation — but start at one row of three, best first:
 * reviews pinned or featured in Judge.me, then higher ratings, then ones with
 * a title, then longer bodies. Load more reveals three at a time.
 */
(function () {
  const root = document.querySelector('[data-cs-reviews]');
  if (!root) return;

  const list = root.querySelector('[data-cs-reviews-list]');
  const moreBtn = root.querySelector('[data-cs-reviews-more]');
  const endpoint = root.dataset.endpoint;
  const anon = root.dataset.anon;
  const handle = root.dataset.handle;
  const SHOW = 3;
  const FETCH = 100; // the edge function's page; covers every product but Card Slinger in one call
  const BODY_MAX = 190;

  let page = 0;
  let exhausted = false; // no more pages to fetch
  let queue = []; // fetched, sorted, not yet shown

  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"]/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])
    );

  // Same rule as cs-reviews-strip.js, including the 0.72 rule that avoids
  // cutting a word almost at the limit.
  const truncate = (body) => {
    const n = String(body || '').replace(/\s+/g, ' ').trim();
    if (n.length <= BODY_MAX) return n;
    const slice = n.slice(0, BODY_MAX);
    const lastSpace = slice.lastIndexOf(' ');
    const cutoff = lastSpace > BODY_MAX * 0.72 ? lastSpace : BODY_MAX;
    return slice.slice(0, cutoff).replace(/[\s.,;:!?—-]+$/, '') + '…';
  };

  const score = (r) =>
    (r.pinned || r.featured ? 1e6 : 0) +
    (Number(r.rating) || 0) * 1e4 +
    (r.title ? 1e3 : 0) +
    Math.min(String(r.body || '').trim().length, 999);

  const stars = (n) => (window.csStars ? window.csStars(n, 16) : '');

  const card = (r) => {
    const pics = (r.pictures || [])
      .slice(0, 4)
      .map(
        (p) =>
          `<img src="${esc((p.urls && (p.urls.small || p.urls.original)) || '')}" alt="" loading="lazy" class="block w-10 h-10 object-cover border border-border">`
      )
      .join('');

    return (
      `<li class="cs-ink bg-foreground text-background p-6 flex flex-col gap-3">` +
      `<div class="flex items-center gap-2 flex-wrap">${stars(r.rating)}</div>` +
      (r.title
        ? `<h3 class="tracking-tight text-xl md:text-2xl leading-[1]" style="font-family:'Beleren','Helvetica Neue',sans-serif">${esc(r.title)}</h3>`
        : '') +
      `<p class="text-sm leading-relaxed font-light" style="font-family:'Merriweather',Georgia,serif">${esc(truncate(r.body))}</p>` +
      `<div class="flex flex-col gap-1 mt-auto pt-1">` +
      `<p class="mono text-xs text-background/60">— ${esc((r.reviewer && r.reviewer.name) || 'Anonymous')}</p>` +
      `</div>` +
      (pics ? `<div class="flex flex-wrap gap-1">${pics}</div>` : '') +
      `</li>`
    );
  };

  const headers = { Authorization: `Bearer ${anon}`, apikey: anon };
  const get = (params) =>
    fetch(`${endpoint}?${new URLSearchParams(params)}`, { headers }).then((r) =>
      r.ok ? r.json() : Promise.reject(new Error(String(r.status)))
    );

  /**
   * Judge.me keys reviews by its own product id, not Shopify's, so the handle
   * has to be resolved first — the same two-step fetchProductReviews() does.
   */
  let productId = null;
  const resolveId = () =>
    productId
      ? Promise.resolve(productId)
      : get({ action: 'product_id', handle }).then((d) => (productId = d && d.id));

  const fetchPage = () => {
    page += 1;
    return resolveId()
      .then((id) => {
        if (!id) throw new Error('no judge.me product id');
        return get({ action: 'product_reviews', product_id: id, page, per_page: FETCH });
      })
      .then((data) => {
        const all = (data && data.reviews) || [];
        if (!Array.isArray(all)) throw new Error('unexpected payload');
        if (all.length < FETCH) exhausted = true;
        const fresh = all.filter((r) => !r.hidden && String(r.body || '').trim());
        queue = queue.concat(fresh.sort((a, b) => score(b) - score(a)));
      });
  };

  const showMore = () =>
    (queue.length >= SHOW || exhausted ? Promise.resolve() : fetchPage()).then(() => {
      list.insertAdjacentHTML('beforeend', queue.splice(0, SHOW).map(card).join(''));
      moreBtn.classList.toggle('hidden', !queue.length && exhausted);
    });

  // Star-only reviews still count toward the summary up top, so a product can
  // have a rating but no written review to show; drop the empty grid then.
  showMore()
    .catch(() => moreBtn.classList.add('hidden'))
    .then(() => {
      if (!list.children.length) root.remove();
    });

  moreBtn.addEventListener('click', () => {
    moreBtn.disabled = true;
    showMore()
      .catch(() => moreBtn.classList.add('hidden'))
      .finally(() => {
        moreBtn.disabled = false;
      });
  });
})();
